import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Input,
  AutoComplete,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  DatePicker,
  InputNumber,
  message,
  Statistic,
  Row,
  Col,
  Tooltip,
  List,
  Grid,
  Upload
} from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  LinkOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile } from 'antd/es/upload/interface'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useVehicleStore } from '../stores/vehicleStore'
import { useAuthStore } from '../stores/authStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { app, ensureLogin } from '../services/tcbClient'
import type { Vehicle, VehicleStatus } from '../types'
import { getActiveLeaseStart, getVehicleDueDate } from './vehicleManagerUtils'

const { Search } = Input
const { Option } = Select

const statusConfigMap: Record<string, { color: string; text: string }> = {
  available: { color: 'success', text: '可用' },
  rented: { color: 'processing', text: '出租中' },
  maintenance: { color: 'warning', text: '退租' },
  inactive: { color: 'default', text: '销售' },
  contracted: { color: 'cyan', text: '承包车' }
}

const rentPeriodOptions = [
  { value: 'monthly', label: '月付' },
  { value: 'quarterly', label: '季付' },
  { value: 'halfyear', label: '半年付' },
  { value: 'yearly', label: '年付' },
  { value: 'custom', label: '自定义' }
]
const reminderPeriodOptions = [
  { value: 'monthly', label: '月付' },
  { value: 'quarterly', label: '季付' },
  { value: 'halfyear', label: '半年付' },
  { value: 'yearly', label: '年付' },
  { value: 'custom', label: '自定义' }
]
const receivableModeOptions = [
  { value: 'daily_365', label: '按365日均摊' },
  { value: 'monthly_plan', label: '按年金/收款月数' }
]
const areaOptions = ['A', 'B', 'C', 'D']
const normalizePlate = (value?: string) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[—–－]/g, '-')

const VehicleListPage: React.FC = () => {
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [contractUploadList, setContractUploadList] = useState<UploadFile[]>([])
  const [existingContractFiles, setExistingContractFiles] = useState<string[]>([])
  const [form] = Form.useForm()
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  const fillForm = (vehicle: Vehicle) => {
    const leaseHistory = Array.isArray((vehicle as any).leaseHistory) ? (vehicle as any).leaseHistory : []
    const pricingHistory = Array.isArray((vehicle as any).pricingHistory) ? (vehicle as any).pricingHistory : []
    const hasHistoricalStart = Boolean(
      (vehicle as any).currentLeaseId ||
      leaseHistory.length ||
      pricingHistory.some((item: any) => String(item?.startDate || '').trim())
    )
    const startDateValue = String(vehicle.startDate || '').trim() || (hasHistoricalStart ? getActiveLeaseStart(vehicle) : '')
    const reminderPeriodValue = String((vehicle as any).reminderPeriod || vehicle.rentPeriod || 'yearly').trim() || 'yearly'
    const reminderAmountValue = vehicle.reminderAmount ?? vehicle.rentAmount
    const statusValue: VehicleStatus =
      vehicle.status ||
      (((vehicle as any).currentLeaseId || String(vehicle.company || '').trim())
        ? 'rented'
        : 'available')
    const dueDateValue = String(vehicle.dueDate || '').trim() || (
      startDateValue
        ? (getVehicleDueDate({
            ...vehicle,
            startDate: startDateValue,
            dueDate: '',
            reminderPeriod: reminderPeriodValue,
            reminderAmount: reminderAmountValue,
            rentPeriod: vehicle.rentPeriod || 'yearly',
            status: statusValue
          } as Vehicle) || '')
        : ''
    )

    form.setFieldsValue({
      ...vehicle,
      status: statusValue,
      dueDate: dueDateValue ? dayjs(dueDateValue) : null,
      reminderDate: vehicle.reminderDate ? dayjs(vehicle.reminderDate) : null,
      reminderPeriod: reminderPeriodValue,
      reminderPeriodMonths: vehicle.reminderPeriodMonths,
      reminderAmount: reminderAmountValue,
      startDate: startDateValue ? dayjs(startDateValue) : null,
      rentPeriod: 'yearly',
      rentPeriodMonths: undefined,
      receivableMode: (vehicle as any).receivableMode || 'daily_365',
      receivableMonths: (vehicle as any).receivableMonths,
      rentChangeStart: null,
      rentChangeAmount: undefined
    })
  }

  const {
    vehicles,
    statistics,
    loading,
    fetchVehicles,
    fetchStatistics,
    addVehicle,
    updateVehicle,
    deleteVehicle
  } = useVehicleStore()

  const { user, checkPermission } = useAuthStore()
  const canEdit = checkPermission('manage_vehicles')

  const companyProfiles = useMemo(() => {
    const sorted = [...vehicles].sort((a, b) => {
      const ta = dayjs((a as any).updatedAt || (a as any).createdAt).valueOf()
      const tb = dayjs((b as any).updatedAt || (b as any).createdAt).valueOf()
      return tb - ta
    })
    const map = new Map<string, {
      company: string
      contactPerson?: string
      contactPerson2?: string
      phone?: string
      phone2?: string
      address?: string
      configuration?: string
    }>()
    const pick = (current?: string, incoming?: string) => {
      const c = String(current || '').trim()
      if (c) return c
      const v = String(incoming || '').trim()
      return v || ''
    }
    for (const item of sorted) {
      const company = String(item.company || '').trim()
      if (!company) continue
      const key = company.toLowerCase()
      const prev = map.get(key)
      map.set(key, {
        company,
        contactPerson: pick(prev?.contactPerson, item.contactPerson),
        contactPerson2: pick(prev?.contactPerson2, item.contactPerson2),
        phone: pick(prev?.phone, item.phone),
        phone2: pick(prev?.phone2, item.phone2),
        address: pick(prev?.address, item.address),
        configuration: pick(prev?.configuration, (item as any).configuration || item.notes)
      })
    }
    return map
  }, [vehicles])

  const companyOptions = useMemo(
    () =>
      Array.from(companyProfiles.values()).map(profile => ({
        value: profile.company,
        label: `${profile.company}${profile.contactPerson ? `（${profile.contactPerson}` : ''}${profile.phone ? ` / ${profile.phone}` : ''}${profile.contactPerson ? '）' : ''}${profile.address ? `  ${profile.address}` : ''}`
      })),
    [companyProfiles]
  )

  const applyCompanyProfile = (companyName?: string, force = false) => {
    const normalizeCompanyKey = (value?: string) =>
      String(value || '').trim().toLowerCase().replace(/\s+/g, '')

    const keyword = normalizeCompanyKey(companyName)
    if (!keyword) return

    let profile = companyProfiles.get(keyword)
    if (!profile) {
      for (const item of companyProfiles.values()) {
        const candidate = normalizeCompanyKey(item.company)
        if (candidate.startsWith(keyword)) {
          profile = item
          break
        }
      }
    }
    if (!profile) {
      for (const item of companyProfiles.values()) {
        const candidate = normalizeCompanyKey(item.company)
        if (candidate.includes(keyword) || keyword.includes(candidate)) {
          profile = item
          break
        }
      }
    }
    if (!profile) return

    const current = form.getFieldsValue(['contactPerson', 'contactPerson2', 'phone', 'phone2', 'address', 'configuration'])
    const next: Record<string, string> = {}
    const assign = (field: keyof typeof current, value?: string) => {
      if (!value) return
      if (force || !current[field]) next[field] = value
    }
    assign('contactPerson', profile.contactPerson)
    assign('contactPerson2', profile.contactPerson2)
    assign('phone', profile.phone)
    assign('phone2', profile.phone2)
    assign('address', profile.address)
    assign('configuration', profile.configuration)
    if (Object.keys(next).length) {
      form.setFieldsValue(next)
    }
  }

  useEffect(() => {
    const load = async () => {
      try {
        await fetchVehicles()
        await fetchStatistics()
      } catch (err) {
        console.error('加载车辆数据失败', err)
        messageApi.error('加载车辆数据失败')
      }
    }
    load()
  }, [fetchVehicles, fetchStatistics])

  const handleSearch = async (value: string) => {
    setCurrentPage(1)
    setSearchKeyword(value.trim())
  }

  const matchesKeyword = (text: any, keyword: string) => {
    if (!keyword) return true
    const source = String(text || '').toLowerCase()
    const needle = keyword.toLowerCase()
    if (!source) return false
    if (source.includes(needle)) return true
    let idx = 0
    for (const ch of needle) {
      idx = source.indexOf(ch, idx)
      if (idx === -1) return false
      idx += 1
    }
    return true
  }

  const filteredVehicles = vehicles.filter(v => {
    if (statusFilter === 'all') return true
    return v.status === statusFilter
  }).filter(v => {
    const keyword = searchKeyword.trim()
    if (!keyword) return true
    return (
      matchesKeyword(v.plate, keyword) ||
      matchesKeyword(v.company, keyword) ||
      matchesKeyword(v.contactPerson, keyword) ||
      matchesKeyword(v.contactPerson2, keyword) ||
      matchesKeyword(v.phone, keyword) ||
      matchesKeyword(v.phone2, keyword)
    )
  })

  const handleStatusCardClick = (status: string) => {
    setStatusFilter(status)
    setCurrentPage(1)
  }

  const renderDueDate = (date?: string) => {
    const dueDate = date ? dayjs(date) : null
    if (!dueDate || !dueDate.isValid()) return <span style={{ color: '#999' }}>未设置</span>
    const now = dayjs()
    const diff = dueDate.diff(now, 'day')
    let color = '#000'
    if (diff < 0) color = 'var(--danger)'
    else if (diff <= 7) color = 'var(--warning)'
    else if (diff <= 30) color = '#faad14'
    return (
      <span style={{ color }}>
        {dueDate.format('YYYY-MM-DD')}
        {diff < 0 && <div style={{ fontSize: 12 }}>已逾期 {Math.abs(diff)} 天</div>}
        {diff >= 0 && diff <= 30 && <div style={{ fontSize: 12 }}>剩余 {diff} 天</div>}
      </span>
    )
  }

  const columns: ColumnsType<Vehicle> = [
    {
      title: '车牌号',
      dataIndex: 'plate',
      key: 'plate',
      fixed: 'left',
      width: 120,
      render: (text: string) => <span style={{ fontWeight: 500, color: 'var(--accent)' }}>{text}</span>
    },
    { title: '车型', dataIndex: 'type', key: 'type', width: 120 },
    { title: '承租公司', dataIndex: 'company', key: 'company', width: 150, ellipsis: true },
    { title: '联系人', dataIndex: 'contactPerson', key: 'contactPerson', width: 120 },
    { title: '联系人2', dataIndex: 'contactPerson2', key: 'contactPerson2', width: 120 },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (phone: string) => (
        <Space>
          <span>{phone}</span>
          {phone && (
            <Button type="link" size="small" icon={<PhoneOutlined />} onClick={() => window.open(`tel:${phone}`)} />
          )}
        </Space>
      )
    },
    {
      title: '联系电话2',
      dataIndex: 'phone2',
      key: 'phone2',
      width: 140,
      render: (phone: string) => (
        <Space>
          <span>{phone}</span>
          {phone && (
            <Button type="link" size="small" icon={<PhoneOutlined />} onClick={() => window.open(`tel:${phone}`)} />
          )}
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: VehicleStatus) => {
        const config = statusConfigMap[String(status)] || { color: 'default', text: status || '未知' }
        return <Tag color={config.color}>{config.text}</Tag>
      }
    },
    {
      title: '片区',
      dataIndex: 'area',
      key: 'area',
      width: 90,
      render: (area?: string) => area || '-'
    },
    {
      title: '起租日',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 130,
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-')
    },
    {
      title: '到期日',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 140,
      render: (date: string) => renderDueDate(date)
    },
    {
      title: '租金',
      dataIndex: 'rentAmount',
      key: 'rentAmount',
      width: 100,
      render: (amount: number) => (amount ? `￥${amount}` : '-')
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      width: 240,
      ellipsis: true,
      render: (address: string) =>
        address ? (
          <Tooltip title={address}>
            <Space>
              <EnvironmentOutlined />
              {address}
            </Space>
          </Tooltip>
        ) : (
          '-'
        )
    },
    {
      title: '合同',
      key: 'contract',
      width: 120,
      render: (_, record) => {
        const files = Array.isArray((record as any).contractFiles) ? (record as any).contractFiles : []
        if (!files.length) return <Tag color="red">未上传</Tag>
        return (
          <Button size="small" icon={<LinkOutlined />} onClick={() => handleOpenContract(files[0])}>
            查看({files.length})
          </Button>
        )
      }
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => handleOpenLease(record)}
            disabled={!canEdit}
          >
            换租
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} disabled={!canEdit}>
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            disabled={!canEdit}
          >
            删除
          </Button>
        </Space>
      )
    }
  ]

  const handleAdd = () => {
    setEditingVehicle(null)
    form.resetFields()
    form.setFieldsValue({ status: 'rented' })
    setContractUploadList([])
    setExistingContractFiles([])
    setIsModalVisible(true)
  }

  const handleEdit = (vehicle: Vehicle) => {
    form.resetFields()
    setEditingVehicle(vehicle)
    setContractUploadList([])
    setExistingContractFiles(Array.isArray((vehicle as any).contractFiles) ? (vehicle as any).contractFiles : [])
    setIsModalVisible(true)
  }

  const handleOpenLease = (vehicle: Vehicle) => {
    const nextId = vehicle._id || vehicle.id || vehicle.plate
    navigate(`/vehicles/${nextId}/lease-transfer`)
  }

  const getTempFileUrl = async (fileId: string) => {
    if (!fileId) return ''
    if (!String(fileId).startsWith('cloud://')) return String(fileId)
    await ensureLogin()
    const storage = (app as any).storage?.()
    const fileList = [{ fileID: fileId, maxAge: 24 * 60 * 60 }]
    const res: any = storage?.getTempFileURL
      ? await storage.getTempFileURL({ fileList })
      : await (app as any).getTempFileURL({ fileList })
    return res?.fileList?.[0]?.tempFileURL || res?.fileList?.[0]?.tempFileUrl || res?.fileList?.[0]?.download_url || ''
  }

  const handleOpenContract = async (fileId: string) => {
    try {
      const url = await getTempFileUrl(fileId)
      if (!url) {
        messageApi.error('合同链接无效')
        return
      }
      window.open(url, '_blank')
    } catch (err) {
      console.error(err)
      messageApi.error('打开合同失败')
    }
  }

  const handleDeleteExistingContract = (fileId: string, index: number) => {
    Modal.confirm({
      title: '确认删除这份合同？',
      content: '删除后车辆档案里将不再显示这份合同。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        const nextFiles = existingContractFiles.filter((item, idx) => !(idx === index && item === fileId))
        setExistingContractFiles(nextFiles)
        if (editingVehicle) {
          const docId = editingVehicle._id || editingVehicle.id
          await updateVehicle(docId, { _id: docId, contractFiles: nextFiles } as any)
          await Promise.all([fetchVehicles(), fetchStatistics()])
          messageApi.success('合同已删除')
        }
      }
    })
  }

  useEffect(() => {
    if (isModalVisible && editingVehicle) {
      fillForm(editingVehicle)
    } else if (isModalVisible && !editingVehicle) {
      form.setFieldsValue({ status: 'rented' })
    }
  }, [isModalVisible, editingVehicle])

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      // 年金核验：如果年租金 = 月提醒金额，警告用户可能填错
      const rentAmt = Number(values.rentAmount || 0)
      const remindAmt = Number(values.reminderAmount ?? values.rentAmount ?? 0)
      const remindPeriod = values.reminderPeriod || 'monthly'
      if (rentAmt > 0 && remindPeriod === 'monthly' && Math.abs(rentAmt - remindAmt) < 1) {
        await new Promise<void>((resolve, reject) => {
          Modal.confirm({
            title: '年租金核验提醒',
            content: `年租金 ¥${rentAmt.toFixed(0)} 与月提醒金额 ¥${remindAmt.toFixed(0)} 相同。\n年租金通常是月租金的12倍，若月租为 ¥${remindAmt.toFixed(0)}，则年租应填 ¥${(remindAmt * 12).toFixed(0)}。\n请确认年租金是否正确？`,
            okText: '确认正确，继续保存',
            cancelText: '我再检查一下',
            onOk: () => resolve(),
            onCancel: () => reject(new Error('用户取消，请重新确认年租金'))
          })
        })
      }
      values.plate = normalizePlate(values.plate)
      values.rentPeriod = 'yearly'
      values.rentPeriodMonths = undefined
      const { rentChangeStart, rentChangeAmount } = values as any
      const reminderPeriod = values.reminderPeriod
      const reminderPeriodMonths = reminderPeriod === 'custom' ? Number(values.reminderPeriodMonths || 1) : undefined
      const receivableMode = values.receivableMode || 'daily_365'
      const receivableMonths = receivableMode === 'monthly_plan' ? Number(values.receivableMonths || 12) : undefined
      const docId = editingVehicle?._id || editingVehicle?.id
      const baseStart = values.startDate
        ? values.startDate.format('YYYY-MM-DD')
        : (editingVehicle as any)?.startDate || dayjs().format('YYYY-MM-DD')
      const effectiveStart = rentChangeStart ? rentChangeStart.format('YYYY-MM-DD') : baseStart
      const effectiveAmount = rentChangeAmount ? Number(rentChangeAmount) : Number(values.rentAmount || 0)
      const normalizedPricingHistory = [{
        startDate: effectiveStart,
        rentAmount: effectiveAmount,
        rentPeriod: 'yearly'
      }]
      const payload = {
        id: docId || values.id,
        ...values,
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : '',
        reminderDate: values.reminderDate ? values.reminderDate.format('YYYY-MM-DD') : '',
        reminderPeriodMonths,
        receivableMode,
        receivableMonths,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : '',
        status: values.status || 'available',
        pricingHistory: normalizedPricingHistory,
        leaseHistory: (editingVehicle as any)?.leaseHistory,
        currentLeaseId: (editingVehicle as any)?.currentLeaseId,
        rentAmount: effectiveAmount
      }
      const uploadedContracts: string[] = []
      for (const file of contractUploadList) {
        if (!file.originFileObj) continue
        const plateText = values.plate || editingVehicle?.plate || 'unknown'
        const cloudPath = `vehicle_contracts/${plateText}-${Date.now()}-${file.name}`
        const res: any = await app.uploadFile({
          cloudPath,
          filePath: file.originFileObj
        } as any)
        if (res?.fileID) uploadedContracts.push(res.fileID)
      }
      ;(payload as any).contractFiles = [...existingContractFiles, ...uploadedContracts]
      // 去掉仅用于表单的字段，避免 Dayjs 内部属性写入
      delete (payload as any).rentChangeStart
      delete (payload as any).rentChangeAmount
      if (reminderPeriod !== 'custom') {
        delete (payload as any).reminderPeriodMonths
      }
      if (receivableMode !== 'monthly_plan') {
        delete (payload as any).receivableMonths
      }
      // 调试：确认实际提交的载荷
      console.log('update vehicle payload', payload)
      if (editingVehicle) {
        const oldRent = Number(editingVehicle.rentAmount || 0)
        const newRent = Number(effectiveAmount || 0)
        if (Math.abs(newRent - oldRent) > 0.009) {
          const warnRes = await simpleDirectCloudApi.addRentAdjustRequest({
            vehicleId: editingVehicle._id || editingVehicle.id || editingVehicle.plate,
            plate: editingVehicle.plate,
            company: values.company || editingVehicle.company,
            currentRent: oldRent,
            requestedRent: newRent,
            notes: '系统检测到租金已修改，请总管理员核对是否有申请单。',
            status: 'pending',
            createdBy: user?.nickName || user?.id || 'system'
          })
          if (!warnRes.success) {
            messageApi.error(warnRes.error || '无法创建租金核对提醒，本次修改已阻止')
            return
          }
        }
        await updateVehicle(editingVehicle._id || editingVehicle.id, payload)
        messageApi.success('更新成功')
      } else {
        await addVehicle(payload as any)
        messageApi.success('新增成功')
      }
      setIsModalVisible(false)
      setContractUploadList([])
      setExistingContractFiles([])
      await Promise.all([fetchVehicles(), fetchStatistics()])
    } catch (error: any) {
      messageApi.error(error?.message || '提交失败')
    }
  }

  const handleDelete = (vehicle: Vehicle) => {
    Modal.confirm({
      title: '确认删除车辆？',
      content: `确认删除车辆 ${vehicle.plate} 吗？`,
      okText: '删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteVehicle(vehicle._id || vehicle.id)
          messageApi.success('删除成功')
          fetchVehicles()
          fetchStatistics()
        } catch (error: any) {
          messageApi.error(error?.message || '删除失败')
        }
      }
    })
  }

  return (
    <div>
      {contextHolder}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card hoverable onClick={() => handleStatusCardClick('all')} style={{ cursor: 'pointer' }}>
              <Statistic title="车辆总数" value={statistics.total} valueStyle={{ color: 'var(--accent)' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable onClick={() => handleStatusCardClick('maintenance')} style={{ cursor: 'pointer' }}>
              <Statistic title="退租车" value={statistics.maintenance} valueStyle={{ color: 'var(--warning)' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable onClick={() => handleStatusCardClick('rented')} style={{ cursor: 'pointer' }}>
              <Statistic title="出租中" value={statistics.rented} valueStyle={{ color: 'var(--accent)' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card hoverable onClick={() => handleStatusCardClick('contracted')} style={{ cursor: 'pointer' }}>
              <Statistic title="承包车" value={statistics.contracted} valueStyle={{ color: '#13c2c2' }} />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space wrap>
                <Search
                  placeholder="车牌 / 公司 / 联系人 / 电话..."
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="middle"
                  style={{ width: '100%', maxWidth: 320 }}
                  onSearch={handleSearch}
                />
                <Select
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value)
                    setCurrentPage(1)
                  }}
                  style={{ width: '100%', maxWidth: 160 }}
                  size="middle"
                >
                  <Option value="all">全部状态</Option>
                  <Option value="available">可用</Option>
                  <Option value="rented">出租中</Option>
                  <Option value="maintenance">退租</Option>
                  <Option value="contracted">承包车</Option>
                  <Option value="inactive">销售</Option>
                </Select>
              </Space>
            </Col>
            <Col>
              {canEdit && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                  新增车辆
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {isMobile ? (
          <List
            dataSource={filteredVehicles}
            loading={loading}
            rowKey={(record) => record._id || record.id || record.plate}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredVehicles.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
              onChange: (page, size) => {
                setCurrentPage(page)
                if (size !== pageSize) {
                  setPageSize(size)
                  setCurrentPage(1)
                }
              },
              onShowSizeChange: (_current, size) => {
                setPageSize(size)
                setCurrentPage(1)
              }
            }}
            renderItem={(record) => {
              const statusConfig = statusConfigMap[String(record.status)] || { color: 'default', text: record.status || '未知' }
              return (
                <List.Item
                  style={{ padding: '16px 8px' }}
                  actions={[
                    <Button
                      key="lease"
                      type="link"
                      size="small"
                      onClick={() => handleOpenLease(record)}
                      disabled={!canEdit}
                    >
                      换租
                    </Button>,
                    <Button
                      key="edit"
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(record)}
                      disabled={!canEdit}
                    >
                      编辑
                    </Button>,
                    <Button
                      key="delete"
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(record)}
                      disabled={!canEdit}
                    >
                      删除
                    </Button>
                  ]}
                >
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    <Space wrap>
                      <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{record.plate}</span>
                      <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                    </Space>
                    <Space size={8} wrap>
                      <span>车型：{record.type || '-'}</span>
                      <span>租金：{record.rentAmount ? `￥${record.rentAmount}` : '-'}</span>
                    </Space>
                    <div style={{ color: '#666' }}>承租公司：{record.company || '-'}</div>
                    <Space size={8} wrap>
                      <span>联系人：{record.contactPerson || '-'}</span>
                      {record.phone ? (
                        <Button type="link" size="small" icon={<PhoneOutlined />} onClick={() => window.open(`tel:${record.phone}`)}>
                          {record.phone}
                        </Button>
                      ) : (
                        <span>电话：-</span>
                      )}
                    </Space>
                    {record.address && (
                      <Space size={6} wrap>
                        <EnvironmentOutlined />
                        <span style={{ wordBreak: 'break-all' }}>{record.address}</span>
                      </Space>
                    )}
                    <div>片区：{record.area || '-'}</div>
                    <div>到期日：{renderDueDate(record.dueDate)}</div>
                    <div>
                      合同：
                      {Array.isArray((record as any).contractFiles) && (record as any).contractFiles.length ? (
                        <Button type="link" size="small" onClick={() => handleOpenContract((record as any).contractFiles[0])}>
                          查看({(record as any).contractFiles.length})
                        </Button>
                      ) : (
                        <Tag color="red">未上传</Tag>
                      )}
                    </div>
                  </Space>
                </List.Item>
              )
            }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredVehicles}
            rowKey={(record) => record._id || record.id || record.plate}
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredVehicles.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
              onChange: (page, size) => {
                setCurrentPage(page)
                if (size !== pageSize) {
                  setPageSize(size)
                  setCurrentPage(1)
                }
              },
              onShowSizeChange: (_current, size) => {
                setPageSize(size)
                setCurrentPage(1)
              }
            }}
          />
        )}
      </Card>

      <Modal
        title={editingVehicle ? '编辑车辆' : '新增车辆'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false)
          setEditingVehicle(null)
          form.resetFields()
          setContractUploadList([])
          setExistingContractFiles([])
        }}
        width={640}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="plate"
                label="车牌号"
                rules={[
                  { required: true, message: '请输入车牌号' },
                  {
                    validator: (_, value) => {
                      const normalized = normalizePlate(value)
                      if (!normalized) return Promise.resolve()
                      const duplicated = vehicles.some(v => {
                        if (editingVehicle && String(v._id || v.id) === String(editingVehicle._id || editingVehicle.id)) return false
                        return normalizePlate(v.plate) === normalized
                      })
                      return duplicated
                        ? Promise.reject(new Error('这辆车已存在，不能重复新增'))
                        : Promise.resolve()
                    }
                  }
                ]}
              >
                <Input placeholder="请输入车牌号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label="车型" rules={[{ required: true, message: '请输入车型' }]}>
                <Input placeholder="请输入车型" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="company" label="承租公司" rules={[{ required: true, message: '请输入承租公司' }]}>
                <AutoComplete
                  options={companyOptions}
                  filterOption={(input, option) =>
                    String(option?.value || '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onSelect={(value) => applyCompanyProfile(String(value), true)}
                  onChange={(value) => applyCompanyProfile(String(value), false)}
                  onBlur={() => applyCompanyProfile(form.getFieldValue('company'), false)}
                >
                  <Input placeholder="输入公司名自动联想并带出联系人/电话/地址" />
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contactPerson" label="联系人">
                <Input placeholder="请输入联系人" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contactPerson2" label="联系人2">
                <Input placeholder="可选，第二联系人" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone2" label="联系电话2">
                <Input placeholder="可选，第二联系电话" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="phone" label="联系电话" rules={[{ required: true, message: '请输入联系电话' }]}>
                <Input placeholder="请输入联系电话" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select placeholder="请选择状态">
                  <Option value="available">可用</Option>
                  <Option value="rented">出租中</Option>
                  <Option value="maintenance">退租</Option>
                  <Option value="contracted">承包车</Option>
                  <Option value="inactive">销售</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="startDate" label="起租日" rules={[{ required: true, message: '请选择起租日' }]}>
                <DatePicker style={{ width: '100%' }} placeholder="选择起租日" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dueDate" label="到期日" rules={[{ required: true, message: '请选择到期日' }]}>
                <DatePicker style={{ width: '100%' }} placeholder="选择到期日" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="area" label="片区归属">
                <Select placeholder="选择片区">
                  {areaOptions.map(area => (
                    <Option key={area} value={area}>{area}片区</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="reminderDate" label="到期提醒日">
                <DatePicker style={{ width: '100%' }} placeholder="设置提醒日期" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="receivableMode"
                label="代收算法"
                initialValue="daily_365"
                rules={[{ required: true, message: '请选择代收算法' }]}
              >
                <Select placeholder="请选择代收算法">
                  {receivableModeOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.receivableMode !== cur.receivableMode}>
                {() => {
                  const mode = form.getFieldValue('receivableMode') || 'daily_365'
                  if (mode !== 'monthly_plan') {
                    return (
                      <Form.Item label="收款月数">
                        <InputNumber style={{ width: '100%' }} disabled placeholder="仅按年金/收款月数模式可填" />
                      </Form.Item>
                    )
                  }
                  return (
                    <Form.Item
                      name="receivableMonths"
                      label="收款月数"
                      rules={[{
                        validator: (_, v) => {
                          const n = Number(v || 0)
                          if (Number.isFinite(n) && n > 0) return Promise.resolve()
                          return Promise.reject(new Error('请输入大于0的月数'))
                        }
                      }]}
                    >
                      <InputNumber style={{ width: '100%' }} min={1} max={24} placeholder="例如 10 或 11" />
                    </Form.Item>
                  )
                }}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="提醒周期" required>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.reminderPeriod !== cur.reminderPeriod}
                >
                  {() => {
                    const period = form.getFieldValue('reminderPeriod') || 'monthly'
                    return (
                      <Input.Group compact>
                        <Form.Item
                          name="reminderPeriod"
                          noStyle
                          rules={[{ required: true, message: '请选择提醒周期' }]}
                          initialValue="monthly"
                        >
                          <Select
                            style={{ width: period === 'custom' ? '60%' : '100%' }}
                            placeholder="选择提醒周期"
                            onChange={(val) => {
                              if (val !== 'custom') {
                                form.setFieldValue('reminderPeriodMonths', undefined)
                              }
                            }}
                          >
                            {reminderPeriodOptions.map(opt => (
                              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                        {period === 'custom' && (
                          <Form.Item
                            name="reminderPeriodMonths"
                            noStyle
                            rules={[{
                              validator: (_, v) => {
                                if (!v || Number(v) <= 0) return Promise.reject(new Error('请输入大于0的月数'))
                                return Promise.resolve()
                              }
                            }]}
                          >
                            <InputNumber
                              style={{ width: '40%' }}
                              min={1}
                              placeholder="月数"
                            />
                          </Form.Item>
                        )}
                      </Input.Group>
                    )
                  }}
                </Form.Item>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reminderAmount" label="提醒金额" rules={[{ required: true, message: '请输入提醒金额' }]}>
                <InputNumber style={{ width: '100%' }} placeholder="请输入提醒金额" min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="付款周期" required>
                <Form.Item name="rentPeriod" noStyle initialValue="yearly">
                  <Select style={{ width: '100%' }} disabled>
                    <Option value="yearly">年付</Option>
                  </Select>
                </Form.Item>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rentAmount" label="租金" rules={[{ required: true, message: '请输入租金' }]}>
                <InputNumber style={{ width: '100%' }} placeholder="请输入租金" min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="depositAmount" label="押金">
                <InputNumber style={{ width: '100%' }} placeholder="请输入押金" min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="rentChangeStart" label="租金调整生效日">
                <DatePicker style={{ width: '100%' }} placeholder="选填，调整从何时开始" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rentChangeAmount" label="调整后租金">
                <InputNumber style={{ width: '100%' }} placeholder="选填，生效后的租金" min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="address" label="地址">
                <Input placeholder="请输入地址" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="configuration" label="备注">
            <Input.TextArea rows={3} placeholder="配置/备注" />
          </Form.Item>

          <Form.Item label="车辆合同（可选）">
            <Upload
              fileList={contractUploadList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setContractUploadList(fileList)}
              onRemove={(file) => {
                setContractUploadList(prev => prev.filter(it => it.uid !== file.uid))
                return true
              }}
            >
              <Button>上传合同图片/PDF</Button>
            </Upload>
            {existingContractFiles.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: '#666', marginBottom: 4 }}>已上传合同</div>
                <Space wrap>
                  {existingContractFiles.map((fileId, index) => (
                    <Space.Compact key={`${fileId}-${index}`}>
                      <Button size="small" icon={<LinkOutlined />} onClick={() => handleOpenContract(fileId)}>
                        合同{index + 1}
                      </Button>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteExistingContract(fileId, index)}
                      >
                        删除
                      </Button>
                    </Space.Compact>
                  ))}
                </Space>
              </div>
            )}
          </Form.Item>

        </Form>
      </Modal>

    </div>
  )
}

export default VehicleListPage

